import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputBase,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from "@mui/icons-material/Search";

const StyledChip = ({ label, status }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      backgroundColor:
        status === "FAILED"
          ? "#ffebee"
          : status === "MANUAL"
          ? "#e3f2fd"
          : status === "PASSED"
          ? "#e8f5e9"
          : undefined,
      color:
        status === "FAILED"
          ? "#d32f2f"
          : status === "MANUAL"
          ? "#1976d2"
          : status === "PASSED"
          ? "#2e7d32"
          : undefined,
      fontWeight: 600,
      fontSize: "0.75rem",
      height: 24,
      "& .MuiChip-label": {
        padding: "0 8px",
      },
    }}
  />
);

// Severity color mapping
const SEVERITY_COLORS = {
  CRITICAL: "#b71c1c",
  HIGH: "#f44336",
  MEDIUM: "#fbc02d",
  LOW: "#4caf50",
  INFORMATIONAL: "#1976d2",
  DEFAULT: "#bdbdbd",
};

const ChecksGrid = ({ checks = [] }) => {
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Check if Region and Status columns should be shown
  const hasRegionAndStatus = useMemo(() => {
    if (checks.length === 0) return true; // Default to showing
    return checks.some(check => check.Region || check.Status);
  }, [checks]);

  // Filtered rows
  const filteredRows = useMemo(() =>
    checks.filter(
      (row) =>
        row.CheckId?.toLowerCase().includes(search.toLowerCase()) ||
        row.CheckTitle?.toLowerCase().includes(search.toLowerCase()) ||
        row.Name?.toLowerCase().includes(search.toLowerCase()) ||
        row.Description?.toLowerCase().includes(search.toLowerCase()) ||
        row.Severity?.toLowerCase().includes(search.toLowerCase()) ||
        row.Status?.toLowerCase().includes(search.toLowerCase()) ||
        row.Service?.toLowerCase().includes(search.toLowerCase())
    ),
    [checks, search]
  );

  // Reset to first page when search changes
  React.useEffect(() => {
    setPage(0);
  }, [search]);

  // Pagination logic
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRow(null); // Collapse any expanded row on page change
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRow(null);
  };

  return (
    <Box sx={{ flexGrow: 1, overflow: "hidden", height: "calc(100vh - 200px)" }}>
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', background: '#F1F1F1', borderRadius: 1, px: 1, height: 36 }}>
            <SearchIcon sx={{ color: '#888', fontSize: 20, mr: 1 }} />
            <InputBase
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: 200, fontSize: 14 }}
            />
          </Box>
        </Stack>
      </Paper>
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0px 4px 12px rgba(0,0,0,0.08)', mb: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: 60, fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}></TableCell>
              <TableCell align="left" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Title</TableCell>
              {hasRegionAndStatus && (
                <>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Region</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Status</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row, idx) => {
              const globalIdx = page * rowsPerPage + idx;
              const sevColor = SEVERITY_COLORS[(row.Severity || '').toUpperCase()] || SEVERITY_COLORS.DEFAULT;
              const checkTitle = row.CheckTitle || row.Name || row.CheckId;
              
              return (
                <React.Fragment key={row.CheckId + '-' + globalIdx}>
                  <TableRow hover selected={expandedRow === globalIdx} onClick={() => setExpandedRow(expandedRow === globalIdx ? null : globalIdx)} style={{ cursor: 'pointer' }}>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === globalIdx ? null : globalIdx); }}
                        aria-label={expandedRow === globalIdx ? 'Collapse row' : 'Expand row'}
                      >
                        {expandedRow === globalIdx ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell align="left">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: sevColor, display: 'inline-block', mr: 1 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#222' }}>{checkTitle}</Typography>
                      </Box>
                    </TableCell>
                    {hasRegionAndStatus && (
                      <>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: 14, color: '#222' }}>{row.Region || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 14,
                              color:
                                row.Status === "FAILED"
                                  ? "#d32f2f"
                                  : row.Status === "MANUAL"
                                  ? "#1976d2"
                                  : row.Status === "PASSED"
                                  ? "#2e7d32"
                                  : "#888",
                            }}
                          >
                            {row.Status || 'N/A'}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                  {expandedRow === globalIdx && (
                    <TableRow>
                      <TableCell colSpan={hasRegionAndStatus ? 4 : 2} sx={{ background: '#f8fafd', borderBottom: '1px solid #e3eafc', p: 2 }}>
                        <Box>
                          {row.Description && (
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              <b>Description:</b> {row.Description}
                            </Typography>
                          )}
                          {row.Remediation && (
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              <b>Remediation:</b> {typeof row.Remediation === 'string' ? row.Remediation : row.Remediation?.Text}
                              {row.Remediation?.Url && (
                                <a href={row.Remediation.Url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, color: '#1976d2', textDecoration: 'underline' }}>Reference</a>
                              )}
                            </Typography>
                          )}
                          {row.Applicable !== undefined && row.Applicable !== null && (
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              <b>Applicable:</b> {String(row.Applicable)}
                            </Typography>
                          )}
                          {row.Service && (
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              <b>Service:</b> {row.Service}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Footer: Severity legend and pagination */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F5F5F5', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 16px 16px', px: 2, py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Severity Levels:
          </Typography>
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'].map((sev) => (
            <React.Fragment key={sev}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: SEVERITY_COLORS[sev], display: 'inline-block', mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: SEVERITY_COLORS[sev], mr: 1 }}>
                {sev.charAt(0) + sev.slice(1).toLowerCase()}
              </Typography>
            </React.Fragment>
          ))}
        </Stack>
        <Box>
          <Paper elevation={0} sx={{ background: 'none', boxShadow: 'none' }}>
            <Stack direction="row" alignItems="center">
              <Typography variant="caption" sx={{ mr: 1 }}>
                Rows per page:
              </Typography>
              <select value={rowsPerPage} onChange={handleChangeRowsPerPage} style={{ fontSize: 14, borderRadius: 4, padding: '2px 8px', border: '1px solid #ccc', marginRight: 8 }}>
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <Typography variant="caption" sx={{ mr: 1 }}>
                {filteredRows.length === 0 ? 0 : page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredRows.length)} of {filteredRows.length}
              </Typography>
              <IconButton size="small" onClick={() => handleChangePage(null, Math.max(page - 1, 0))} disabled={page === 0}>
                {'<'}
              </IconButton>
              <IconButton size="small" onClick={() => handleChangePage(null, Math.min(page + 1, Math.ceil(filteredRows.length / rowsPerPage) - 1))} disabled={page >= Math.ceil(filteredRows.length / rowsPerPage) - 1}>
                {'>'}
              </IconButton>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ChecksGrid;
